import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { debounce, find, uniq } from "lodash";
import * as React from "react";
import { FormGroup, Input, Label, Button } from "../components/Modal";
import { GrantTypeMap } from "./ClientsTable";
import * as ClientStore from "./ClientStore";

const { Component } = React;

interface ClientScope {
    scope: string;
}

export class Client {
    constructor() {
        this.grantTypes = [];
        this.redirectUris = [];
        this.postLogoutRedirectUris = [];
        this.properties = [];
        this.scopes = [];
    }

    id: string;
    name: string;
    allowOfflineAccess: boolean;
    allowAccessTokensViaBrowser: boolean;
    requireConsent: boolean;
    requireClientSecret: boolean;
    disableContext: boolean;
    grantTypes: Array<string>;
    redirectUris: Array<string>;
    postLogoutRedirectUris: Array<string>;
    backChannelLogoutUri: string;
    properties: Array<KeyValue>;
    scopes: Array<ClientScope>;
    uri: string;
    logoUri: string;
    requirePKCE: boolean;
}

interface KeyValue {
    key: string;
    value: string;
}

interface EditClientProps {
    organization: string;
    updated: Function;
    clientId: string;
}

interface EditClientState {
    client: Client;
    isLoaded: boolean;
    error?: any;
    hasApiAccess: boolean;
    hasRegistrationEnabled: boolean;
    registrationUri: string;
    newRedirectUri: string;
    newPostLogoutRedirectUri: string;
    disableContext: boolean;
}

const API_ACCESSS_KEY = "permissions.identityApi";
const REGISTRATION_ALLOWED_KEY = "registration.enabled";
const REGISTRATION_URI_KEY = "registration.uri";
const DISABLE_CONTEXT = "context.disabled";
const CONFINED_TYPES = "confinedTypes";

export const EditClient = ({ clientId, organization, updated }: EditClientProps) => {
    const [client, setClient] = React.useState(new Client());
    const [isLoaded, setIsLoaded] = React.useState(false);
    const [hasApiAccess, setHasApiAccess] = React.useState(false);
    const [hasRegistrationEnabled, setHasRegistrationEnabled] = React.useState(false);
    const [registrationUri, setRegistrationUri] = React.useState("");
    const [newRedirectUri, setNewRedirectUri] = React.useState("");
    const [newPostLogoutRedirectUri, setNewPostLogoutRedirectUri] = React.useState("");
    const [disableContext, setDisableContext] = React.useState(false);
    const [error, setError] = React.useState<any>(undefined);

    let editClient = (client) => {
        return ClientStore.UpdateClient(client).then(
            (result) => {
                setIsLoaded(true);
                setClient(result.data.client);

                updated && updated(result.data.client);
            },
            (error) => {
                setIsLoaded(true);

                setError(error);

                updated && updated(null);
            }
        );
    };

    const debounceEditClient = React.useCallback(
        debounce((client) => {
            editClient(client);
        }, 500),
        []
    );

    const debounceHandleRegistrationUriChange = React.useCallback(
        debounce((value) => {
            ClientStore.UpdateClientProperties(client.id, REGISTRATION_URI_KEY, value).then((result) => {
                // props.updated(result.client);
            });
        }, 500),
        [client]
    );

    React.useEffect(() => {
        ClientStore.GetClient(clientId).then((res) => {
            setClient(res.data);
            setIsLoaded;
        });
    }, []);

    React.useEffect(() => {
        if (client.id) {
            setHasApiAccess(client.properties.filter((g) => g.key == API_ACCESSS_KEY && g.value == "true").length > 0);
            setHasRegistrationEnabled(client.properties.filter((g) => g.key == REGISTRATION_ALLOWED_KEY && g.value == "true").length > 0);
            setRegistrationUri((find(client.properties, { key: REGISTRATION_URI_KEY }) || {}).value);
            setDisableContext(client.properties.filter((g) => g.key == DISABLE_CONTEXT && g.value == "true").length > 0);
        }
    }, [client]);

    React.useEffect(() => {
        if (client.id) {
            debounceEditClient(client);
        }
    }, [
        client.name,
        client.backChannelLogoutUri,
        client.uri,
        client.logoUri,
        client.allowOfflineAccess,
        client.requireConsent,
        client.requireClientSecret,
        // client.grantTypes,
        // client.redirectUris,
        // client.postLogoutRedirectUris,
        // client.properties,
        // client.scopes,
    ]);

    let handleNameChange = (e) => {
        var name = e.target.value;

        setClient({ ...client, name });
    };

    let handleBackChannelLogoutUriChange = (e) => {
        var backChannelLogoutUri = e.target.value;

        setClient({ ...client, backChannelLogoutUri });
    };

    let handleUriChange = (e) => {
        var uri = e.target.value;
        setClient({ ...client, uri });
    };

    let handleLogoUriChange = (e) => {
        var logoUri = e.target.value;
        setClient({ ...client, logoUri });
    };

    let handleOfflineAccessChange = (e) => {
        var allowOfflineAccess = e.target.checked;
        setClient({ ...client, allowOfflineAccess });
    };

    let handleAllowAccessTokensViaBrowser = (e) => {
        var allowAccessTokensViaBrowser = e.target.checked;
        setClient({ ...client, allowAccessTokensViaBrowser });
    };

    let handleRequireConsentChange = (e) => {
        var requireConsent = e.target.checked;
        setClient({ ...client, requireConsent });
    };

    let handleRequireSecretChange = (e) => {
        var requireClientSecret = e.target.checked;
        setClient({ ...client, requireClientSecret });
    };

    let handleApiAccessChange = (e) => {
        var value = e.target.checked;

        setHasApiAccess(value),
            ClientStore.UpdateClientProperties(client.id, API_ACCESSS_KEY, value).then((result) => {
                updated(result.data.client);
            });
    };

    let handleRegistrationAllowedChange = (e) => {
        var value = e.target.checked;

        setHasRegistrationEnabled(value);

        ClientStore.UpdateClientProperties(client.id, REGISTRATION_ALLOWED_KEY, value).then((result) => {
            updated(result.data.client);
        });
    };

    let handleDisableContextChange = (e) => {
        var value = e.target.checked;

        setDisableContext(value);

        ClientStore.UpdateClientProperties(client.id, DISABLE_CONTEXT, value).then((result) => {
            updated(result.data.client);
        });
    };

    let handleNewRedirectUriChange = (e) => {
        var value = e.target.value;

        setNewRedirectUri(value);
    };

    let handleNewPostLogoutRedirectUriChange = (e) => {
        var value = e.target.value;

        setNewPostLogoutRedirectUri(value);
    };

    let handleRegistrationUriChange = (e) => {
        var value = e.target.value;
        setRegistrationUri(value);
        debounceHandleRegistrationUriChange(value);
    };

    let createRedirectUri = () => {
        ClientStore.CreateClientRedirectUri(client.id, newRedirectUri).then(() => {
            ClientStore.GetClient(client.id).then((result) => {
                setClient(result.data);
                setNewRedirectUri("");
            });
        });
    };

    let removeRedirectUri = (redirectUri) => {
        ClientStore.RemoveClientRedirectUri(client.id, redirectUri).then(() => {
            ClientStore.GetClient(client.id).then((result) => {
                setClient(result.data);
            });
        });
    };

    let createPostLogoutRedirectUri = () => {
        ClientStore.CreateClientPostLogoutRedirectUri(client.id, newPostLogoutRedirectUri).then(() => {
            ClientStore.GetClient(client.id).then((result) => {
                setClient(result.data);
                setNewPostLogoutRedirectUri("");
            });
        });
    };

    let removePostLogoutRedirectUri = (postLogoutRedirectUri) => {
        ClientStore.RemoveClientPostLogoutRedirectUri(client.id, postLogoutRedirectUri).then(() => {
            ClientStore.GetClient(client.id).then((result) => {
                setClient(result.data);
            });
        });
    };

    let addGrantType = (grantType) => () => {
        let grantTypes = client.grantTypes.concat(grantType);
        grantTypes = uniq(grantTypes);
        setClient({ ...client, grantTypes });
        debounceEditClient({ ...client, grantTypes });
    };

    let removeGrantType = (grantType) => () => {
        let grantTypes = client.grantTypes.filter((s) => s != grantType);
        setClient({ ...client, grantTypes });
        debounceEditClient({ ...client, grantTypes });
    };

    let addScope = (scope) => () => {
        let scopes = client.scopes.concat(scope);
        scopes = uniq(scopes);
        setClient({ ...client, scopes });
        setClient({ ...client, scopes });
    };

    let removeScope = (scope) => () => {
        let scopes = client.scopes.filter((s) => s != scope);
        setClient({ ...client, scopes });
        setClient({ ...client, scopes });
    };

    let handleRequirePKCEChange = (e) => {
        var requirePKCE = e.target.checked;

        setClient({ ...client, requirePKCE });
        setClient({ ...client, requirePKCE });
    };

    let addConfinedType = (type) => () => {
        let confinedTypes = find(client.properties, { key: CONFINED_TYPES });

        let updateValue = type;

        if (confinedTypes) {
            updateValue = confinedTypes.value + "," + type;
        }

        ClientStore.UpdateClientProperties(client.id, CONFINED_TYPES, updateValue).then((result) => {
            updated(result.data.client);
            ClientStore.GetClient(clientId).then((res) => {
                setClient(res.data);
                setIsLoaded;
            });
        });
    };

    let removeConfinedType = (type) => () => {
        let confinedTypes = find(client.properties, { key: CONFINED_TYPES });

        let updateValue = null;

        if (confinedTypes) {
            updateValue = confinedTypes.value
                .split(",")
                .filter((s) => s != type)
                .join(",");
        }

        ClientStore.UpdateClientProperties(client.id, CONFINED_TYPES, updateValue).then((result) => {
            updated(result.data.client);
            ClientStore.GetClient(clientId).then((res) => {
                setClient(res.data);
                setIsLoaded;
            });
        });
    };

    const update = () => {
        editClient(client);
    };

    return (
        <>
            {client && (
                <div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                        <FormGroup>
                            <Label>Name</Label>
                            <Input type="text" value={client.name} onChange={handleNameChange} />
                        </FormGroup>

                        <FormGroup>
                            <Label className="flex items-center">
                                <Input type="checkbox" id="offlineAccess" checked={client.allowOfflineAccess} onChange={handleOfflineAccessChange} className="mr-2" />
                                <span>Allow Offline Authentication</span>
                            </Label>
                        </FormGroup>

                        <FormGroup>
                            <Label className="flex items-center">
                                <Input type="checkbox" id="accessTokensViaBrowser" checked={client.allowAccessTokensViaBrowser} onChange={handleAllowAccessTokensViaBrowser} className="mr-2" />
                                <span>Allow Access Tokens Via Browser</span>
                            </Label>
                        </FormGroup>

                        <FormGroup>
                            <Label className="flex items-center">
                                <Input type="checkbox" id="requireConsent" checked={client.requireConsent} onChange={handleRequireConsentChange} className="mr-2" />
                                <span>Require User Consent</span>
                            </Label>
                        </FormGroup>

                        <FormGroup>
                            <Label className="flex items-center">
                                <Input type="checkbox" id="requireSecret" checked={client.requireClientSecret} onChange={handleRequireSecretChange} className="mr-2" />
                                <span>Require User Secret</span>
                            </Label>
                        </FormGroup>

                        <FormGroup>
                            <Label className="flex items-center">
                                <Input type="checkbox" id="context" checked={disableContext} onChange={handleDisableContextChange} className="mr-2" />
                                <span>Disable Context for Client</span>
                            </Label>
                        </FormGroup>

                        <FormGroup>
                            <Label className="flex items-center">
                                <Input type="checkbox" id="apiAccess" checked={hasApiAccess} onChange={handleApiAccessChange} className="mr-2" />
                                <span>Admin Role</span>
                            </Label>
                        </FormGroup>

                        <FormGroup>
                            <Label className="flex items-center">
                                <Input type="checkbox" id="registrationEnabled" checked={hasRegistrationEnabled} onChange={handleRegistrationAllowedChange} className="mr-2" />
                                <span>Allow customer registration</span>
                            </Label>
                        </FormGroup>

                        <FormGroup>
                            <Label className="flex items-center">
                                <Input type="checkbox" id="requirePKCE" checked={client.requirePKCE} onChange={handleRequirePKCEChange} className="mr-2" />
                                <span>Require PKCE</span>
                            </Label>
                        </FormGroup>

                        <FormGroup>
                            <Label>Registration URI</Label>
                            <Input type="text" value={registrationUri || ""} onChange={handleRegistrationUriChange} />
                        </FormGroup>

                        <FormGroup>
                            <Label>Redirect URIs</Label>
                            <div className="space-y-2">
                                {client.redirectUris &&
                                    client.redirectUris.map((r, key) => (
                                        <div key={"redirectUri" + key} className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                                            <span className="text-sm text-gray-900 break-all">{r}</span>
                                            <Button variant="outline-danger" size="sm" onClick={() => removeRedirectUri(r)} className="ml-2 flex-shrink-0">
                                                <FontAwesomeIcon icon={["fad", "trash"]}></FontAwesomeIcon>
                                            </Button>
                                        </div>
                                    ))}
                                <div className="flex space-x-2">
                                    <Input 
                                        type="text" 
                                        value={newRedirectUri} 
                                        onChange={handleNewRedirectUriChange}
                                        placeholder="Add new redirect URI"
                                        className="flex-1"
                                    />
                                    <Button variant="outline-success" size="sm" onClick={createRedirectUri} className="flex-shrink-0">
                                        <FontAwesomeIcon icon={["fad", "plus"]}></FontAwesomeIcon>
                                    </Button>
                                </div>
                            </div>
                        </FormGroup>

                        <FormGroup>
                            <Label>Post Logout Redirect URIs</Label>
                            <div className="space-y-2">
                                {client.postLogoutRedirectUris &&
                                    client.postLogoutRedirectUris.map((r, key) => (
                                        <div key={"postLogoutRedirectUri" + key} className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                                            <span className="text-sm text-gray-900 break-all">{r}</span>
                                            <Button variant="outline-danger" size="sm" onClick={() => removePostLogoutRedirectUri(r)} className="ml-2 flex-shrink-0">
                                                <FontAwesomeIcon icon={["fad", "trash"]}></FontAwesomeIcon>
                                            </Button>
                                        </div>
                                    ))}
                                <div className="flex space-x-2">
                                    <Input 
                                        type="text" 
                                        value={newPostLogoutRedirectUri || ""} 
                                        onChange={handleNewPostLogoutRedirectUriChange}
                                        placeholder="Add new post logout redirect URI"
                                        className="flex-1"
                                    />
                                    <Button variant="outline-success" size="sm" onClick={createPostLogoutRedirectUri} className="flex-shrink-0">
                                        <FontAwesomeIcon icon={["fad", "plus"]}></FontAwesomeIcon>
                                    </Button>
                                </div>
                            </div>
                        </FormGroup>

                        <FormGroup>
                            <Label>Back Channel Logout URI</Label>
                            <Input type="text" value={client.backChannelLogoutUri || ""} onChange={handleBackChannelLogoutUriChange} />
                        </FormGroup>

                        <FormGroup>
                            <Label>URI</Label>
                            <Input type="text" value={client.uri || ""} onChange={handleUriChange} />
                        </FormGroup>

                        <FormGroup>
                            <Label>Logo URI</Label>
                            <Input type="text" value={client.logoUri || ""} onChange={handleLogoUriChange} />
                        </FormGroup>
                    </div>
                    <div className="space-y-6">
                        <FormGroup>
                            <Label>Grant Types</Label>
                            <div className="space-y-2 mb-4">
                                {client.grantTypes &&
                                    client.grantTypes.map((r, key) => (
                                        <div key={"grantType" + key} className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                                            <span className="text-sm text-gray-900">{GrantTypeMap[r as keyof typeof GrantTypeMap]}</span>
                                            <Button variant="outline-danger" size="sm" onClick={removeGrantType(r)} className="ml-2 flex-shrink-0">
                                                <FontAwesomeIcon icon={["fad", "trash"]}></FontAwesomeIcon>
                                            </Button>
                                        </div>
                                    ))}
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <Button variant="outline-secondary" size="sm" onClick={addGrantType("implicit")}>
                                    + Implicit
                                </Button>
                                <Button variant="outline-secondary" size="sm" onClick={addGrantType("hybrid")}>
                                    + Hybrid
                                </Button>
                                <Button variant="outline-secondary" size="sm" onClick={addGrantType("authorization_code")}>
                                    + Authorization Code
                                </Button>
                                <Button variant="outline-secondary" size="sm" onClick={addGrantType("client_credentials")}>
                                    + Client Credentials
                                </Button>
                                <Button variant="outline-secondary" size="sm" onClick={addGrantType("urn:ietf:params:oauth:grant-type:device_code")}>
                                    + Device Flow
                                </Button>
                                <Button variant="outline-secondary" size="sm" onClick={addGrantType("password")}>
                                    + Password
                                </Button>
                            </div>
                        </FormGroup>

                        <FormGroup>
                            <Label>Scopes</Label>
                            <div className="space-y-2 mb-4">
                                {client.scopes &&
                                    client.scopes.map((r, key) => (
                                        <div key={"scopes" + key} className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                                            <span className="text-sm text-gray-900">{r}</span>
                                            <Button variant="outline-danger" size="sm" onClick={removeScope(r)} className="ml-2 flex-shrink-0">
                                                <FontAwesomeIcon icon={["fad", "trash"]}></FontAwesomeIcon>
                                            </Button>
                                        </div>
                                    ))}
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <Button variant="outline-secondary" size="sm" onClick={addScope("default")}>
                                    + default
                                </Button>
                                <Button variant="outline-secondary" size="sm" onClick={addScope("profile")}>
                                    + profile
                                </Button>
                                <Button variant="outline-secondary" size="sm" onClick={addScope("openid")}>
                                    + openid
                                </Button>
                                <Button variant="outline-secondary" size="sm" onClick={addScope("identity")}>
                                    + identity
                                </Button>
                            </div>
                        </FormGroup>

                        <FormGroup>
                            <Label>Membership Types</Label>
                            <div className="space-y-2 mb-4">
                                {find(client.properties, { key: CONFINED_TYPES }) &&
                                    find(client.properties, { key: CONFINED_TYPES })
                                        ?.value?.split(",")
                                        .map((r, key) => (
                                            <div key={"confinedTypes" + key} className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                                                <span className="text-sm text-gray-900">{r}</span>
                                                <Button variant="outline-danger" size="sm" onClick={removeConfinedType(r)} className="ml-2 flex-shrink-0">
                                                    <FontAwesomeIcon icon={["fad", "trash"]}></FontAwesomeIcon>
                                                </Button>
                                            </div>
                                        ))}
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <Button variant="outline-secondary" size="sm" onClick={addConfinedType("Staff")}>
                                    + Staff
                                </Button>
                                <Button variant="outline-secondary" size="sm" onClick={addConfinedType("Parent")}>
                                    + Parent
                                </Button>
                                <Button variant="outline-secondary" size="sm" onClick={addConfinedType("Student")}>
                                    + Student
                                </Button>
                                <Button variant="outline-secondary" size="sm" onClick={addConfinedType("Guest")}>
                                    + Guest
                                </Button>
                            </div>
                        </FormGroup>
                    </div>
                </div>
                
                    <div className="mt-8 flex justify-end">
                        <Button variant="success" onClick={update}>
                            Update Client
                        </Button>
                    </div>
                </div>
            )}
        </>
    );
};
